import styled from '@emotion/styled'
import SVG from '../../assets/svg'
import { color, font } from '../../styles'
import { padding, size } from '../../styles/size'
import Avatar from './Avatar'
import { useState } from 'react'
import { AnimatePresence } from 'framer-motion'
import { Button } from '@mui/material'

type RoleType = 'tenant' | 'manager' | 'owner' | 'prospect' | 'vendor' | 'employee'

interface Props {
   role: {
      id: string
      displayName: string
      email: string
      type: RoleType
      avatarImage?: string | null
      address?: string | null
      companyName?: string | null
      badges?: string[] | null
   }
   loading?: boolean
   showActionMenu?: boolean
   showEditAvatar?: boolean
   onGoToPortal?: (roleId: string, roleType: RoleType, isOnboardingRequired: boolean) => void
   onDeleteClick?: (roleId: string, roleType: RoleType) => void
   onAvatarClick?: (roleId: string, roleType: RoleType) => void
   isOnboardingRequired: boolean
}

const RoleCard: React.FC<Props> = (props) => {
   const {
      role,
      loading,
      showActionMenu,
      showEditAvatar,
      isOnboardingRequired,
      onGoToPortal,
      onDeleteClick,
      onAvatarClick,
   } = props
   const { id, displayName, email, avatarImage, address, companyName, badges, type } = role
   const isHolderClickable = !showActionMenu && !showEditAvatar
   const [revealActionMenu, setRevealActionMenu] = useState<boolean>(false)

   // ==============
   // Event Handlers
   // ==============
   const handleAvatarClick = () => {
      onAvatarClick?.(id, type)
   }

   const handleGoToPortalClick = () => {
      onGoToPortal?.(id, type, isOnboardingRequired)
   }

   const handleDeleteClick = () => {
      onDeleteClick?.(id, type)
   }

   const handleHolderClick = () => {
      isHolderClickable && onGoToPortal?.(id, type, isOnboardingRequired)
   }

   const handleMenuIconClick = () => {
      setRevealActionMenu(!revealActionMenu)
   }

   // =======
   // Renders
   // =======
   const classNames = `${type} ${loading ? 'loading' : ''} ${
      isHolderClickable ? 'clickable' : ''
   } ${showActionMenu ? 'show-action-menu' : ''} ${
      isOnboardingRequired ? 'onboarding-required' : ''
   }`

   return (
      <Holder>
         <div className={'container ' + classNames} onClick={handleHolderClick}>
            <Avatar
               roles={[{ avatarImage: avatarImage, displayName }]}
               showEditAvatar={showEditAvatar}
               loading={loading}
               onClick={handleAvatarClick}
            />
            <div className="info">
               <h4 className="name">{displayName}</h4>
               <p className="company-name">{companyName}</p>
               <address className="address">
                  <SVG.form.email className="icon" />
                  <span className="content">{email}</span>
               </address>
               {address && (
                  <address className="address">
                     <SVG.form.location className="icon" />
                     <span className="content">{address}</span>
                  </address>
               )}
               {badges && badges?.length >= 1 && (
                  <div className="badges-holder">
                     {badges?.map((badge, index) => (
                        <span key={index} className="badge">
                           {badge}
                        </span>
                     ))}
                  </div>
               )}
            </div>
            {showActionMenu && (
               <div className={`menu ${revealActionMenu ? 'reveal' : ''}`}>
                  <Button
                     onClick={handleGoToPortalClick}
                     startIcon={<SVG.form.goto />}
                     color="info"
                     size="small"
                  >
                     Access Portal
                  </Button>
                  <Button
                     onClick={handleDeleteClick}
                     startIcon={<SVG.form.delete />}
                     color="error"
                     size="small"
                  >
                     Remove
                  </Button>
               </div>
            )}
            {!showActionMenu && <p className="role-type">{type}</p>}
            {showActionMenu && (
               <div
                  className={`role-type-button ${revealActionMenu ? 'reveal' : ''}`}
                  onClick={handleMenuIconClick}
               >
                  <p className="role-type">{type}</p>
                  <div className="menu-icon">
                     {revealActionMenu ? <SVG.form.closeX /> : <SVG.form.menu />}
                  </div>
               </div>
            )}
            {isOnboardingRequired && (
               <div className="onboarding-required" onClick={handleGoToPortalClick}>
                  <p>Complete your company setup</p>
                  <Button variant="contained" color="info" size="small">
                     Start
                  </Button>
               </div>
            )}
         </div>
      </Holder>
   )
}

const managerColor = '#000'
const tenantColor = '#2F2489'
const ownerColor = '#8FAD2A'
const prospectColor = '#721654'
const vendorColor = '#D27638'
const otherColor = '#663819'

const Holder = styled.div`
   container-type: inline-size;

   .container {
      --background-color: #f8f8f8;
      --active-role-color: ${managerColor};
      --custom-inline-padding: min(${padding.inline}, 1.5em);
      --role-type-width: 4.3em;
      --holder-background-color: var(--background-color);

      display: flex;
      gap: var(--custom-inline-padding);
      position: relative;
      padding: ${padding.block} var(--custom-inline-padding);
      width: 100%;
      box-sizing: border-box;
      border-radius: 1.1em;
      overflow: hidden;
      background-color: var(--background-color);
      transition: background-color 0.2s ease-in-out;

      &:before {
         content: '';
         position: absolute;
         z-index: 2;
         top: 0;
         left: 0;
         width: 3px;
         height: 100%;
         background-color: color-mix(in srgb, var(--active-role-color), white 70%);
         z-index: 1;
         transition: background-color 0.2s ease-in-out;
      }

      .info {
         display: flex;
         flex-direction: column;
         gap: 0.2em;
         flex: 1;
         overflow: hidden;

         p,
         span,
         h4 {
            margin: 0;
            padding: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
         }

         .name {
            color: ${color.violet};
            width: calc(100% - var(--role-type-width) - 1.8em);
         }

         .company-name {
            color: ${color.violet};
            ${font.regular};
            margin-bottom: 0.3em;
         }

         .address {
            display: flex;
            align-items: center;
            gap: 0.6em;
            color: ${color.grayDark};

            .icon {
               width: 1em;
               height: 1em;
               fill: ${color.grayDark};
            }
         }

         .badges-holder {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5em;
            width: 100%;
            overflow: hidden;
            margin-top: 0.2em;
            .badge {
               padding: 0.5em 1em;
               border-radius: 0.4em;
               border: 1px solid ${color.grayLight};
               color: ${color.grayDark};
               font-size: ${size.xs};
               line-height: 0.8;
            }
         }
      }

      .role-type {
         position: absolute;
         top: 1em;
         right: var(--custom-inline-padding);
         color: ${color.white};
         background-color: var(--active-role-color);
         font-size: ${size.xs};
         padding: 0.25em 1em;
         text-transform: capitalize;
         border-radius: 1.1em;
         width: var(--role-type-width);
         text-align: center;
         user-select: none;
      }

      .role-type-button {
         position: absolute;
         top: 1.5em;
         right: var(--custom-inline-padding);
         display: flex;
         align-items: center;
         justify-content: space-between;
         gap: 2px;
         z-index: 2;
         height: 25px;
         cursor: pointer;

         .role-type {
            position: initial;
            top: initial;
            right: initial;
            margin: 0;
            height: 25px;
            padding-block: 0em;
            line-height: 1.9;
            transition: all 0.3s ease-in-out;
            opacity: 1;
            border-radius: 1.1em 0 0 1.1em;
         }

         .menu-icon {
            width: 25px;
            height: 25px;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: var(--active-role-color);
            border-radius: 0 2em 2em 0;
            transition: all 0.2s ease-in-out;

            svg {
               width: 13px;
               height: 13px;
               fill: ${color.white};
               pointer-events: none;
            }
         }

         &.reveal {
            .menu-icon {
               border-radius: 2em;
            }
            .role-type {
               opacity: 0;
            }
         }
      }

      .menu {
         position: absolute;
         top: 0;
         left: 0;
         width: 100%;
         height: 100%;
         display: flex;
         align-items: center;
         align-content: center;
         justify-content: center;
         flex-wrap: wrap;
         padding: var(--custom-inline-padding);
         box-sizing: border-box;
         z-index: 2;
         /* create a circle clip mask */
         clip-path: circle(0% at 100% 0);
         transition: all 0.3s ease-in-out;
         overflow: hidden;
         background-color: #f3f3f3ed;
         pointer-events: none;
         visibility: hidden;

         button {
            margin: 0;
            padding: 0.4em 1.5em;
            border-color: currentColor;
         }

         &.reveal {
            clip-path: circle(143% at 100% 0);
            pointer-events: all;
            visibility: visible;
         }
      }

      .onboarding-required {
         position: absolute;
         bottom: 0;
         width: 100%;
         margin-inline: calc(var(--custom-inline-padding) * -1);
         background-color: ${color.violet};
         display: flex;
         justify-content: space-between;
         align-items: center;
         padding-inline: var(--custom-inline-padding);
         box-sizing: border-box;
         cursor: pointer;
         z-index: 2;

         p {
            margin: 0;
            padding: 0;
            color: ${color.white};
            user-select: none;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            ${font.regular};
         }

         button {
            background-color: ${color.white};
            color: ${color.violet};
         }
      }
      /* Rendering Conditions */
      &.loading .info,
      &.loading .role-type {
         opacity: 0.7;
      }

      &.clickable {
         cursor: pointer;

         & > * {
            pointer-events: none;
         }

         &:hover {
            --background-color: #ececec;

            &:before {
               background-color: color-mix(in srgb, var(--active-role-color), white 30%);
            }
         }
      }

      &.onboarding-required {
         padding-bottom: 4.5em;
      }

      /* Role Coloring */
      &.tenant {
         --active-role-color: ${tenantColor};
      }
      &.manager {
         --active-role-color: ${managerColor};
      }
      &.owner {
         --active-role-color: ${ownerColor};
      }
      &.prospect {
         --active-role-color: ${prospectColor};
      }
      &.vendor {
         --active-role-color: ${vendorColor};
      }
      &.employee {
         --active-role-color: ${otherColor};
      }
   }
`
export type { Props as RoleCardProps, RoleType }
export default RoleCard
